import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { Hari } from '../types';
import { SegRule, defaultRule } from '../utils/segmentFill';
import {
  CONFIG, DrawnPanel, FILL_SPEC, H, HALF_SIZE, PANEL_MAX, PANEL_MIN, PT, SCALE, SIDE_NAME, Segment, TD, V, applySegRules, configureEngine, cwHariLines, cwLayout, cwWallNums, drawSceneCCW, drawSceneCW, drawnPanels, findPartnerEnd, getTypeLabel, isCornerEumsu, panelSizeError, rebalanceLine, rebalanceWalls, sceneSize, setActivePanels, wallSegments,
} from '../engine/panelEngine';
import { selectAllOnFocus, useDragOffset, SegmentFillDialog } from './dialogKit';

// ── 패널 수정 다이얼로그 ──
interface PanelEditDialogProps {
  panel: DrawnPanel;
  lt: string; rt: string; size: string;
  sizeExtraError?: string | null; // 구간 끝 패널 흡수 불가 등
  ltPartner?: string | null; // 위/왼쪽 등 LT 끝과 맞닿은 상대 끝 타입
  rtPartner?: string | null;
  onLtChange: (v: string) => void;
  onRtChange: (v: string) => void;
  onSizeChange: (v: string) => void;
  onSave: () => void;
  onOpenSegment?: () => void; // 벽 패널이면 "구간 채우기" 버튼
  onCancel: () => void;
}

const TYPE_OPTIONS: { value: string; label: string; symbol: string }[] = [
  { value: 'yangsu', label: '양수', symbol: '◁' },
  { value: 'eumsu', label: '음수', symbol: '▷' },
  { value: 'corner', label: '코너', symbol: '◿' },
];

const PanelEditDialog: React.FC<PanelEditDialogProps> = ({
  panel, lt, rt, size, sizeExtraError, ltPartner, rtPartner, onLtChange, onRtChange, onSizeChange, onSave, onCancel, onOpenSegment,
}) => {
  const [pos, setPos] = React.useState({ x: Math.max(24, window.innerWidth - 400), y: 80 });
  const dragging = React.useRef(false);
  const dragStart = React.useRef({ mx: 0, my: 0, px: 0, py: 0 });

  const onMouseDown = React.useCallback((e: React.MouseEvent) => {
    dragging.current = true;
    dragStart.current = { mx: e.clientX, my: e.clientY, px: pos.x, py: pos.y };
    e.preventDefault();
  }, [pos]);

  const onMouseMove = React.useCallback((e: React.MouseEvent) => {
    if (!dragging.current) return;
    setPos({
      x: dragStart.current.px + (e.clientX - dragStart.current.mx),
      y: dragStart.current.py + (e.clientY - dragStart.current.my),
    });
  }, []);

  const onMouseUp = React.useCallback(() => { dragging.current = false; }, []);

  const typeLabel = getTypeLabel(lt, rt);
  const sizeError = panelSizeError(size) ?? sizeExtraError ?? null;

  // 회전 각도로 LT/RT 끝이 화면상 어느 쪽인지 계산 (LT = 로컬 −x 끝, RT = 로컬 +x 끝)
  const sideName = (sign: number) => {
    const dx = sign * Math.cos(panel.angle);
    const dy = sign * Math.sin(panel.angle);
    if (Math.abs(dx) >= Math.abs(dy)) return dx < 0 ? '왼쪽' : '오른쪽';
    return dy < 0 ? '위' : '아래';
  };
  const ltName = sideName(-1);
  const rtName = sideName(1);
  // 위/왼쪽 끝을 먼저 표시
  const ltFirst = ltName === '위' || ltName === '왼쪽';

  const typeBtnStyle = (active: boolean): React.CSSProperties => ({
    flex: 1, padding: '6px 4px', cursor: 'pointer', borderRadius: 6,
    border: active ? '2px solid #5C6BC0' : '1px solid #ddd',
    background: active ? '#EDE7F6' : '#fff',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
    fontSize: 11, fontWeight: active ? 'bold' : 'normal',
  });

  const endSection = (name: string, tag: string, value: string, onChange: (v: string) => void, partner: string | null | undefined, orig: string) => (
    <div key={tag} style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 'bold', color: '#555' }}>
        {name} <span style={{ fontWeight: 'normal', color: '#aaa' }}>({tag})</span>
      </label>
      <div style={{ display: 'flex', gap: 6 }}>
        {TYPE_OPTIONS.map(opt => {
          // 코너 여부는 변경 불가 — 코너 끝은 코너 고정, 코너 아닌 끝은 코너로 못 바꿈
          // (코너는 벽 꺾임에만 존재하고, 소유 이동은 길이 재계산이 필요해 타입만으로는 결합 안 됨)
          const disabled = (opt.value === 'corner') !== (orig === 'corner');
          return (
            <button key={opt.value} disabled={disabled} onClick={() => onChange(opt.value)}
              title={disabled ? (orig === 'corner' ? '코너 끝 — 코너 고정' : '코너가 아닌 끝 — 코너로 변경 불가') : undefined}
              style={{ ...typeBtnStyle(value === opt.value), ...(disabled ? { opacity: 0.35, cursor: 'not-allowed' } : {}) }}>
              <span>{opt.symbol}</span>
              <span>{opt.label}</span>
            </button>
          );
        })}
      </div>
      {partner && isCornerEumsu(value, partner) && (
        <div style={{ marginTop: 4, fontSize: 12, fontWeight: 'bold', color: '#e53935' }}>⚠ 코너와 결합 불가</div>
      )}
    </div>
  );
  const ltSection = endSection(ltName, 'LT', lt, onLtChange, ltPartner, panel.lt);
  const rtSection = endSection(rtName, 'RT', rt, onRtChange, rtPartner, panel.rt);

  return (
    <div
      style={{
        position: 'fixed', left: pos.x, top: pos.y, width: 340, zIndex: 1000,
        background: '#fff', borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.25)', userSelect: 'none',
      }}
      onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
    >
      <div onMouseDown={onMouseDown} style={{
        background: '#e53935', color: '#fff', padding: '10px 16px',
        borderRadius: '10px 10px 0 0', cursor: 'grab',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{ fontWeight: 'bold', fontSize: 14 }}>
          패널 #{panel.num} ({typeLabel}번) 수정
        </span>
        <button onClick={onCancel} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer', padding: '0 2px' }}>✕</button>
      </div>

      <div style={{ padding: 16 }}>
        {/* 사이즈 */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 'bold', color: '#555' }}>패널 사이즈 (mm)</label>
          <input type="number" value={size} onChange={e => onSizeChange(e.target.value)} onFocus={selectAllOnFocus} autoFocus
            style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: `1px solid ${sizeError ? '#e53935' : '#ddd'}`, borderRadius: 4, boxSizing: 'border-box' }}
            step={10} min={PANEL_MIN} max={PANEL_MAX} />
          {sizeError && (
            <div style={{ marginTop: 4, fontSize: 12, fontWeight: 'bold', color: '#e53935' }}>⚠ {sizeError}</div>
          )}
        </div>

        {/* 양 끝 (화면 기준 위/아래 또는 왼쪽/오른쪽) */}
        {ltFirst ? [ltSection, rtSection] : [rtSection, ltSection]}

        {/* 결과 미리보기 */}
        <div style={{ marginBottom: 12, padding: 8, background: '#f5f5f5', borderRadius: 6, textAlign: 'center' }}>
          <span style={{ fontSize: 12, color: '#666' }}>변경 결과: </span>
          <span style={{ fontSize: 14, fontWeight: 'bold', color: '#e53935' }}>{getTypeLabel(lt, rt)}번</span>
          <span style={{ fontSize: 12, color: '#666' }}> ({lt}/{rt})</span>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          {onOpenSegment && (
            <button onClick={onOpenSegment} title="이 패널이 속한 구간 전체를 규칙으로 채우기"
              style={{ marginRight: 'auto', padding: '7px 12px', fontSize: 13, border: '1px solid #5C6BC0', borderRadius: 4, background: '#fff', color: '#3949ab', cursor: 'pointer', fontWeight: 'bold' }}>📐 구간 채우기</button>
          )}
          <button onClick={onCancel} style={{ padding: '7px 18px', fontSize: 13, border: '1px solid #ddd', borderRadius: 4, background: '#fff', cursor: 'pointer' }}>취소</button>
          <button onClick={onSave} disabled={!!sizeError} style={{ padding: '7px 18px', fontSize: 13, border: 'none', borderRadius: 4, background: '#e53935', color: '#fff', cursor: sizeError ? 'not-allowed' : 'pointer', fontWeight: 'bold', opacity: sizeError ? 0.4 : 1 }}>적용</button>
        </div>
      </div>
    </div>
  );
};

// ?H=7000&V=4000 형태로 도면 크기 지정 (헤드리스 검증용, 기본 5000×5000)
{
  const qp = new URLSearchParams(window.location.search);
  configureEngine({ H: Number(qp.get('H')) || 5000, V: Number(qp.get('V')) || 5000 });
}

const INITIAL_HARIS: Hari[] = (() => {
  // ?haris=left,top,TL,BR 형태로 초기 하리 지정 (헤드리스 캡처 검증용). 미지정 시 기본 4개
  const q = new URLSearchParams(window.location.search).get('haris');
  if (q === null) return [
    { id: 'mid1', hariType: 'middle', wallSide: 'left', position: 2000, width: 1200, height: 1500 },
    { id: 'mid2', hariType: 'middle', wallSide: 'bottom', position: 2000, width: 1200, height: 1500 },
    { id: 'mid3', hariType: 'middle', wallSide: 'right', position: 2000, width: 1200, height: 1500 },
    { id: 'mid4', hariType: 'middle', wallSide: 'top', position: 2000, width: 1200, height: 1500 },
  ] as Hari[];
  const list: Hari[] = [];
  const cornerMap: Record<string, 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight'> = {
    TL: 'topLeft', TR: 'topRight', BL: 'bottomLeft', BR: 'bottomRight',
  };
  q.split(',').filter(Boolean).forEach(tok => {
    if (tok === 'left' || tok === 'bottom' || tok === 'right' || tok === 'top') {
      list.push({ id: `mid_${tok}`, hariType: 'middle', wallSide: tok, position: 2000, width: 1200, height: 1500 });
    } else if (cornerMap[tok]) {
      list.push({ id: `corner_${tok}`, hariType: 'corner', corner: cornerMap[tok], width: 1000, height: 1000 });
    }
  });
  return list;
})();

const HariClockwiseTest: React.FC<{ onBack?: () => void }> = ({ onBack }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cwPanelsRef = useRef<DrawnPanel[]>([]); // 마지막 CW 장면 패널 (클릭 판정용)
  const ccwCanvasRef = useRef<HTMLCanvasElement>(null);
  const [haris, setHaris] = useState<Hari[]>(INITIAL_HARIS);
  const [editHari, setEditHari] = useState<Hari | null>(null);
  const hariDlgDrag = useDragOffset();
  const hariOverlayDown = useRef(false); // 드래그 후 바깥에서 놓을 때 닫히지 않게
  const [editWidth, setEditWidth] = useState('');
  const [editHeight, setEditHeight] = useState('');
  const hariBoundsRef = useRef<{ id: string; x: number; y: number; w: number; h: number; label: string; wallSide: string }[]>([]);
  const ccwHariBoundsRef = useRef<{ id: string; x: number; y: number; w: number; h: number; label: string; wallSide: string }[]>([]);
  let ccwDrawnPanels: DrawnPanel[] = [];
  const draggingHariRef = useRef<string | null>(null);
  const hariDragStart = useRef<{ clientX: number; clientY: number; startPos: number } | null>(null);
  const [draggingHari, setDraggingHari] = useState<string | null>(null);
  const [panelOverrides, setPanelOverrides] = useState<Record<number, { lt?: string; rt?: string; size?: number }>>({});
  const [editPanel, setEditPanel] = useState<DrawnPanel | null>(null);
  const [editPanelLt, setEditPanelLt] = useState('');
  const [editPanelRt, setEditPanelRt] = useState('');
  const [editPanelSize, setEditPanelSize] = useState('');
  const [editPanelIsCW, setEditPanelIsCW] = useState(true); // 오버라이드는 CW에만 적용
  // 구간 채우기 규칙 (CW 벽) — key: 벽:구간 시작 위치
  const [segRules, setSegRules] = useState<Record<string, SegRule>>({});
  const [previewSegRules, setPreviewSegRules] = useState<Record<string, SegRule> | null>(null);
  const [segDialog, setSegDialog] = useState<{ seg: Segment; label: string } | null>(null);
  // 패널 번호 → 소속 벽 구간 (CW 최종 배치 기준). 벽 패널이 아니면 null
  const findSegOf = (num: number): { seg: Segment; label: string } | null => {
    const w = rebalanceWalls(applySegRules(cwLayout(haris), segRules), panelOverrides).walls;
    const nums = cwWallNums(w);
    for (const side of ['top', 'right', 'bottom', 'left'] as const) {
      const idx = nums[side].indexOf(num);
      if (idx < 0) continue;
      const sideSegs = wallSegments(w).filter(sg => sg.side === side);
      const k = sideSegs.findIndex(sg => sg.idxs.includes(idx));
      const seg = sideSegs[k];
      return { seg, label: `${SIDE_NAME[side]} · 구간 ${k + 1}/${sideSegs.length} (${seg.total}mm)` };
    }
    return null;
  };
  const applySegmentRule = (seg: Segment, rule: SegRule | null) => {
    const next = { ...segRules };
    if (rule) next[seg.key] = rule; else delete next[seg.key];
    // 패널 수가 바뀌면 뒤 번호가 밀리므로 패널별 수정값 초기화, 아니면 이 구간 사이즈 수정만 제거
    const count = (r: Record<string, SegRule>) => {
      const w = applySegRules(cwLayout(haris), r);
      return w.top.length + w.right.length + w.bottom.length + w.left.length;
    };
    if (count(next) !== count(segRules)) setPanelOverrides({});
    else {
      const w = rebalanceWalls(applySegRules(cwLayout(haris), segRules), panelOverrides).walls;
      const segNums = seg.idxs.map(i => cwWallNums(w)[seg.side][i]);
      const ov = { ...panelOverrides };
      segNums.forEach(n => { if (ov[n]) { const { size: _s, ...rest } = ov[n]; ov[n] = rest; } });
      setPanelOverrides(ov);
    }
    setSegRules(next);
    setPreviewSegRules(null);
    setSegDialog(null);
  };

  // 클릭 좌표가 패널 영역에 있는지 확인
  const hitTestPanel = (mx: number, my: number, panels?: DrawnPanel[]): { panel: DrawnPanel; side: 'lt' | 'rt' | 'body' } | null => {
    for (const p of (panels ?? drawnPanels)) {
      const dx = mx - p.cx;
      const dy = my - p.cy;
      const cos = Math.cos(-p.angle);
      const sin = Math.sin(-p.angle);
      const lx = dx * cos - dy * sin + p.pl / 2;
      const ly = dx * sin + dy * cos + p.pt / 2;

      if (lx >= -TD && lx <= p.pl + TD && ly >= -2 && ly <= p.pt + 2) {
        // 삼각형 영역 판정: lt는 왼쪽 TD 범위, rt는 오른쪽 TD 범위
        if (lx < TD) return { panel: p, side: 'lt' };
        if (lx > p.pl - TD) return { panel: p, side: 'rt' };
        return { panel: p, side: 'body' };
      }
    }
    return null;
  };

  // 같은 벽에서 resize할 패널 찾기 (900 grow시 스킵, 441 항상 스킵)
  const findResizeTarget = (allPanels: DrawnPanel[], fromPanel: DrawnPanel, baseAngle: number, direction: 'shrink' | 'grow', overrides: Record<number, { lt?: string; rt?: string; size?: number }>): DrawnPanel | null => {
    const fc = Math.cos(fromPanel.angle), fs = Math.sin(fromPanel.angle);
    let current = fromPanel;
    const visited = new Set<number>([fromPanel.num]);
    for (let step = 0; step < 10; step++) {
      // current의 양쪽 끝에서 인접 평행 패널 찾기
      const candidates: { p: DrawnPanel; dist: number }[] = [];
      for (const p of allPanels) {
        if (visited.has(p.num)) continue;
        const angleDiff = Math.abs(baseAngle - p.angle) % Math.PI;
        if (angleDiff > 0.3 && angleDiff < 2.8) continue; // 직교는 스킵
        const pc = Math.cos(p.angle), ps = Math.sin(p.angle);
        const cRtX = current.cx + (current.pl / 2) * fc, cRtY = current.cy + (current.pl / 2) * fs;
        const cLtX = current.cx - (current.pl / 2) * fc, cLtY = current.cy - (current.pl / 2) * fs;
        const pRtX = p.cx + (p.pl / 2) * pc, pRtY = p.cy + (p.pl / 2) * ps;
        const pLtX = p.cx - (p.pl / 2) * pc, pLtY = p.cy - (p.pl / 2) * ps;
        const d1 = Math.hypot(cRtX - pLtX, cRtY - pLtY);
        const d2 = Math.hypot(cLtX - pRtX, cLtY - pRtY);
        const d3 = Math.hypot(cRtX - pRtX, cRtY - pRtY); // 반대 각도 패널용
        const d4 = Math.hypot(cLtX - pLtX, cLtY - pLtY);
        const minD = Math.min(d1, d2, d3, d4);
        if (minD < 40) candidates.push({ p, dist: minD });
      }
      if (candidates.length === 0) return null;
      candidates.sort((a, b) => a.dist - b.dist);
      const next = candidates[0].p;
      const nextSize = overrides[next.num]?.size ?? next.labelSize;
      // 반절(441)은 항상 스킵
      if (nextSize === HALF_SIZE) { visited.add(next.num); current = next; continue; }
      // 표준 폭(900)은 grow시 스킵 (표준 폭 이상으로 늘리지 않음)
      if (direction === 'grow' && nextSize >= CONFIG.MAX_SIZE) { visited.add(next.num); current = next; continue; }
      return next;
    }
    return null;
  };

  // 삼각형 클릭 → 양수/음수 토글 + 인접 패널 자동 반전
  const toggleTriangle = useCallback((panel: DrawnPanel, side: 'lt' | 'rt', panels?: DrawnPanel[]) => {
    const currentVal = side === 'lt' ? panel.lt : panel.rt;
    if (currentVal === 'corner' || currentVal === 'none') return; // corner/none은 토글 불가

    const newVal = currentVal === 'yangsu' ? 'eumsu' : 'yangsu';
    const overrides: Record<number, { lt?: string; rt?: string; size?: number }> = { ...panelOverrides };
    overrides[panel.num] = { ...overrides[panel.num], [side]: newVal };

    // 인접 패널 자동 반전
    const allPanels = panels ?? drawnPanels;
    const mc = Math.cos(panel.angle), ms = Math.sin(panel.angle);
    const sg = side === 'lt' ? -1 : 1;
    const edgeX = panel.cx + sg * (panel.pl / 2) * mc;
    const edgeY = panel.cy + sg * (panel.pl / 2) * ms;

    let bestDist = Infinity;
    let adjPanel: DrawnPanel | null = null;
    let adjSide: 'lt' | 'rt' = 'lt';

    for (const p of allPanels) {
      if (p.num === panel.num) continue;
      const pc = Math.cos(p.angle), ps = Math.sin(p.angle);
      const rtX = p.cx + (p.pl / 2) * pc, rtY = p.cy + (p.pl / 2) * ps;
      const ltX = p.cx - (p.pl / 2) * pc, ltY = p.cy - (p.pl / 2) * ps;
      const dr = Math.hypot(edgeX - rtX, edgeY - rtY);
      const dl = Math.hypot(edgeX - ltX, edgeY - ltY);
      if (dr < bestDist) { bestDist = dr; adjPanel = p; adjSide = 'rt'; }
      if (dl < bestDist) { bestDist = dl; adjPanel = p; adjSide = 'lt'; }
    }

    if (adjPanel && bestDist < 40) {
      const adjVal = adjSide === 'lt' ? adjPanel.lt : adjPanel.rt;
      // 같은 벽(평행) 인접 → 반대 타입으로
      const angleDiff = Math.abs(panel.angle - adjPanel.angle) % Math.PI;
      if (angleDiff < 0.3 || angleDiff > 2.8) {
        // 평행: yangsu↔eumsu 반전
        if (adjVal !== 'corner' && adjVal !== 'none') {
          overrides[adjPanel.num] = { ...overrides[adjPanel.num], [adjSide]: newVal === 'yangsu' ? 'eumsu' : 'yangsu' };
        }
      }
    }

    setPanelOverrides(overrides);
  }, [panelOverrides]);

  // 수정 다이얼로그 선택값 → 오버라이드 (인접 패널 자동 맞춤 포함) — 미리보기·적용 공용
  const buildOverrides = useCallback((ep: DrawnPanel, ltV: string, rtV: string, sizeV: string) => {
    const num = ep.num;
    const newLt = ltV;
    const newRt = rtV;
    const newSize = parseInt(sizeV);
    const overrides: Record<number, { lt?: string; rt?: string; size?: number }> = { ...panelOverrides };
    // 사이즈는 주문 크기에서 바뀐 경우만 오버라이드 (그대로면 미리보기가 위치를 건드리지 않게)
    overrides[num] = { ...overrides[num], lt: newLt, rt: newRt, ...(isNaN(newSize) || newSize === ep.labelSize ? {} : { size: newSize }) };
    // 인접 패널 자동 반전 (위치 기반)
    const me = drawnPanels.find(p => p.num === num);
    if (me) {
      const mc = Math.cos(me.angle), ms = Math.sin(me.angle);
      const findAdj = (ex: number, ey: number) => {
        let bd = Infinity, bp: DrawnPanel | null = null, bs: 'lt'|'rt' = 'lt';
        for (const p of drawnPanels) {
          if (p.num === num) continue;
          const pc = Math.cos(p.angle), ps = Math.sin(p.angle);
          const dr = Math.hypot(ex-(p.cx+(p.pl/2)*pc), ey-(p.cy+(p.pl/2)*ps));
          const dl = Math.hypot(ex-(p.cx-(p.pl/2)*pc), ey-(p.cy-(p.pl/2)*ps));
          if (dr < bd) { bd = dr; bp = p; bs = 'rt'; }
          if (dl < bd) { bd = dl; bp = p; bs = 'lt'; }
        }
        return bd < 40 ? { p: bp!, s: bs } : null;
      };
      const isCornerAdj = (p: DrawnPanel) => {
        const d = Math.abs(me.angle - p.angle) % Math.PI;
        return d > 0.3 && d < 2.8;
      };
      const handle = (side: 'lt'|'rt', nv: string, ov: string) => {
        if (nv === ov) return;
        const sg = side === 'lt' ? -1 : 1;
        const adj = findAdj(me.cx+sg*(me.pl/2)*mc, me.cy+sg*(me.pl/2)*ms);
        if (!adj) return;
        if (isCornerAdj(adj.p)) {
          const isToYangsu = ov === 'corner' && nv === 'yangsu';
          const isToCorner = ov === 'yangsu' && nv === 'corner';
          if (isToYangsu) {
            // corner→yangsu: 직교 패널의 맞닿은 끝만 코너로 (반대쪽 끝은 유지)
            overrides[adj.p.num] = { ...overrides[adj.p.num], [adj.s]: 'corner' };
            // 직교벽 grow: 인접 패널(#18) 찾아서 size 증가
            const perpTarget = findResizeTarget(drawnPanels, adj.p, adj.p.angle, 'grow', overrides);
            if (perpTarget) {
              overrides[perpTarget.num] = { ...overrides[perpTarget.num], size: perpTarget.labelSize + PT + 4 };
            }
          } else if (isToCorner) {
            // yangsu→corner: 직교 패널의 맞닿은 끝만 양수로 (반대쪽 끝은 유지)
            overrides[adj.p.num] = { ...overrides[adj.p.num], [adj.s]: 'yangsu' };
            // 직교벽 shrink: 인접 패널 size override 제거
            const perpTarget = findResizeTarget(drawnPanels, adj.p, adj.p.angle, 'shrink', overrides);
            if (perpTarget) {
              const { size: _s, ...rest } = overrides[perpTarget.num] || {};
              overrides[perpTarget.num] = Object.keys(rest).length > 0 ? rest : undefined as any;
            }
          }
        } else {
          // 평행 인접: yangsu↔eumsu 반전
          if (nv !== 'corner') {
            overrides[adj.p.num] = { ...overrides[adj.p.num], [adj.s]: nv === 'yangsu' ? 'eumsu' : 'yangsu' };
          }
        }
      };
      handle('lt', newLt, ep.lt);
      handle('rt', newRt, ep.rt);
    }
    return overrides;
  }, [panelOverrides]);

  // 다이얼로그 선택값 미리보기 — 적용 전까지 도면에만 반영, 취소하면 원래대로
  // 사이즈 변경 시 구간 끝 패널이 흡수 가능한지 검사 (불가면 경고 + 적용 비활성)
  const rebalanceError = useMemo(() => {
    if (!editPanel || !editPanelIsCW || panelSizeError(editPanelSize)) return null;
    const cand = buildOverrides(editPanel, editPanelLt, editPanelRt, editPanelSize);
    // 하리 내벽 줄 패널이면 줄 안에서 흡수/추가 검증, 아니면 벽 구간 검증
    const line = cwHariLines[editPanel.num];
    if (line) return rebalanceLine(line.length, line.base, line.base.map((_, i) => cand[line.startNum + i]?.size)).error;
    return rebalanceWalls(applySegRules(cwLayout(haris), segRules), cand).errors[editPanel.num] ?? null;
  }, [editPanel, editPanelIsCW, editPanelLt, editPanelRt, editPanelSize, buildOverrides, haris]);
  const previewOverrides = useMemo(
    // 잘못된 사이즈는 미리보기에서 주문 크기 유지 (타입 변경만 반영)
    () => (editPanel && editPanelIsCW
      ? buildOverrides(editPanel, editPanelLt, editPanelRt,
          panelSizeError(editPanelSize) || rebalanceError ? String(editPanel.labelSize) : editPanelSize)
      : null),
    [editPanel, editPanelIsCW, editPanelLt, editPanelRt, editPanelSize, buildOverrides, rebalanceError]
  );
  // 미리보기로 다시 그려진 패널 목록에서 번호로 찾아 상대 끝 타입 조회
  // (위치는 직전 그리기 기준, 타입은 미리보기 오버라이드 우선 — 렌더 시점엔 아직 다시 그려지기 전이라)
  const partnerOf = (side: 'lt' | 'rt') => {
    const me = editPanel && drawnPanels.find(p => p.num === editPanel.num);
    const pe = me && findPartnerEnd(drawnPanels, me, side);
    if (!pe) return null;
    return previewOverrides?.[pe.p.num]?.[pe.s] ?? pe.type;
  };

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const { W, HH } = sceneSize();
    canvas.width = W; canvas.height = HH;
    hariBoundsRef.current = [];
    cwPanelsRef.current = drawSceneCW(ctx, {
      haris, overrides: previewOverrides ?? panelOverrides, segRules: previewSegRules ?? segRules, bounds: hariBoundsRef.current,
    });
  }, [haris, panelOverrides, previewOverrides, segRules, previewSegRules]);

  // ══════════════════════════════════════════
  // CCW(반시계방향) 그리기
  // ══════════════════════════════════════════
  const drawCCW = useCallback(() => {
    const canvas = ccwCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const { W, HH } = sceneSize();
    canvas.width = W; canvas.height = HH;
    ccwHariBoundsRef.current = [];
    ccwDrawnPanels = [...drawSceneCCW(ctx, { haris, overrides: {}, segRules: {}, bounds: ccwHariBoundsRef.current })];
    // 클릭 판정은 CW 목록 기준으로 복원
    setActivePanels(cwPanelsRef.current, previewOverrides ?? panelOverrides);
    // 자동 검증용: 그려진 패널 목록 노출 (헤드리스 검사 스크립트가 읽음)
    (window as any).__scene = { cw: cwPanelsRef.current, ccw: ccwDrawnPanels };
  }, [haris, panelOverrides, previewOverrides]);

  useEffect(() => { draw(); drawCCW(); }, [draw, drawCCW]);

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    // 패널 위 클릭은 패널 수정이 우선 (하리 박스 안 내벽 패널 포함) — 하리는 빈 영역에서만 잡힘
    if (hitTestPanel(mx, my)) return;
    for (const b of hariBoundsRef.current) {
      if (mx >= b.x && mx <= b.x + b.w && my >= b.y && my <= b.y + b.h) {
        const hari = haris.find(h => h.id === b.id);
        if (hari) {
          draggingHariRef.current = hari.id;
          setDraggingHari(hari.id);
          hariDragStart.current = {
            clientX: e.clientX,
            clientY: e.clientY,
            startPos: hari.position!,
          };
          e.preventDefault();
        }
        return;
      }
    }
  }, [haris]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!draggingHariRef.current || !hariDragStart.current) return;
    const hari = haris.find(h => h.id === draggingHariRef.current);
    if (!hari) return;
    if (hari.hariType === 'corner') return; // 코너형은 드래그 없음

    const isHorizontal = hari.wallSide === 'top' || hari.wallSide === 'bottom';
    const delta = isHorizontal
      ? (e.clientX - hariDragStart.current.clientX) / SCALE
      : (e.clientY - hariDragStart.current.clientY) / SCALE;

    const maxLen = isHorizontal ? H : V;
    const MIN_PANEL = 200;
    const minPos = MIN_PANEL;
    const maxPos = maxLen - PT - hari.width - MIN_PANEL;
    const newPos = Math.round(Math.max(minPos, Math.min(maxPos, hariDragStart.current.startPos + delta)));

    setHaris(prev => prev.map(h =>
      h.id === hari.id ? { ...h, position: newPos } : h
    ));
  }, [haris]);

  const handleMouseUp = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const wasHariDrag = !!draggingHariRef.current;
    let wasClick = false;

    if (draggingHariRef.current && hariDragStart.current) {
      const dx = Math.abs(e.clientX - hariDragStart.current.clientX);
      const dy = Math.abs(e.clientY - hariDragStart.current.clientY);
      if (dx <= 3 && dy <= 3) {
        wasClick = true;
        const hari = haris.find(h => h.id === draggingHariRef.current);
        if (hari) {
          setEditHari(hari);
          hariDlgDrag.reset(); // 열 때마다 화면 중앙에서 시작
          setEditWidth(String(hari.hariType === 'corner' ? hari.width : hari.width - PT * 2));
          setEditHeight(String(hari.height ?? 0));
        }
      }
    }

    draggingHariRef.current = null;
    hariDragStart.current = null;
    setDraggingHari(null);

    // 다이얼로그가 열려있으면 캔버스 클릭 무시
    if (editPanel || editHari) return;
    // 하리 드래그/클릭이 아니면 패널 클릭 체크
    if (!wasHariDrag) {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const hit = hitTestPanel(mx, my);
      if (hit) {
        if (hit.side === 'lt' || hit.side === 'rt') {
          // 삼각형 클릭 → 직접 토글
          toggleTriangle(hit.panel, hit.side);
        } else {
          // 본체 클릭 → 수정 다이얼로그
          setEditPanel(hit.panel);
          setEditPanelLt(hit.panel.lt);
          setEditPanelRt(hit.panel.rt);
          setEditPanelSize(String(hit.panel.labelSize));
          setEditPanelIsCW(true);
        }
      }
    }
  }, [haris, toggleTriangle, editPanel, editHari]);

  const handleSave = () => {
    if (!editHari) return;
    const w = parseInt(editWidth);
    const h = parseInt(editHeight);
    if (isNaN(w) || isNaN(h) || w < 200 || h < 200) return;
    setHaris(prev => prev.map(hr =>
      hr.id === editHari.id ? { ...hr, width: editHari.hariType === 'corner' ? w : w + PT * 2, height: h } : hr
    ));
    setEditHari(null);
  };

  // CCW 캔버스 이벤트 핸들러
  const ccwDraggingHariRef = useRef<string | null>(null);
  const ccwHariDragStart = useRef<{ clientX: number; clientY: number; startPos: number } | null>(null);
  const [ccwDraggingHari, setCcwDraggingHari] = useState<string | null>(null);

  const handleCcwMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = ccwCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    if (hitTestPanel(mx, my, ccwDrawnPanels)) return; // 패널 클릭 우선
    for (const b of ccwHariBoundsRef.current) {
      if (mx >= b.x && mx <= b.x + b.w && my >= b.y && my <= b.y + b.h) {
        const hari = haris.find(h => h.id === b.id);
        if (hari) {
          ccwDraggingHariRef.current = hari.id;
          setCcwDraggingHari(hari.id);
          ccwHariDragStart.current = { clientX: e.clientX, clientY: e.clientY, startPos: hari.position! };
          e.preventDefault();
        }
        return;
      }
    }
  }, [haris]);

  const handleCcwMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!ccwDraggingHariRef.current || !ccwHariDragStart.current) return;
    const hari = haris.find(h => h.id === ccwDraggingHariRef.current);
    if (!hari) return;
    if (hari.hariType === 'corner') return; // 코너형은 드래그 없음
    const isHorizontal = hari.wallSide === 'top' || hari.wallSide === 'bottom';
    const delta = isHorizontal
      ? (e.clientX - ccwHariDragStart.current.clientX) / SCALE
      : (e.clientY - ccwHariDragStart.current.clientY) / SCALE;
    const maxLen = isHorizontal ? H : V;
    const MIN_PANEL = 200;
    const minPos = MIN_PANEL;
    const maxPos = maxLen - PT - hari.width - MIN_PANEL;
    const newPos = Math.round(Math.max(minPos, Math.min(maxPos, ccwHariDragStart.current.startPos + delta)));
    setHaris(prev => prev.map(h => h.id === hari.id ? { ...h, position: newPos } : h));
  }, [haris]);

  const handleCcwMouseUp = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const wasHariDrag = !!ccwDraggingHariRef.current;
    if (ccwDraggingHariRef.current && ccwHariDragStart.current) {
      const dx = Math.abs(e.clientX - ccwHariDragStart.current.clientX);
      const dy = Math.abs(e.clientY - ccwHariDragStart.current.clientY);
      if (dx <= 3 && dy <= 3) {
        const hari = haris.find(h => h.id === ccwDraggingHariRef.current);
        if (hari) {
          setEditHari(hari);
          hariDlgDrag.reset(); // 열 때마다 화면 중앙에서 시작
          setEditWidth(String(hari.hariType === 'corner' ? hari.width : hari.width - PT * 2));
          setEditHeight(String(hari.height ?? 0));
        }
      }
    }
    ccwDraggingHariRef.current = null;
    ccwHariDragStart.current = null;
    setCcwDraggingHari(null);

    // 다이얼로그가 열려있으면 캔버스 클릭 무시
    if (editPanel || editHari) return;
    // 패널 클릭 체크 (CCW drawnPanels)
    if (!wasHariDrag) {
      const canvas = ccwCanvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const hit = hitTestPanel(mx, my, ccwDrawnPanels);
      if (hit) {
        if (hit.side === 'lt' || hit.side === 'rt') {
          toggleTriangle(hit.panel, hit.side, ccwDrawnPanels);
        } else {
          setEditPanel(hit.panel);
          setEditPanelLt(hit.panel.lt);
          setEditPanelRt(hit.panel.rt);
          setEditPanelSize(String(hit.panel.labelSize));
          setEditPanelIsCW(false);
        }
      }
    }
  }, [haris, toggleTriangle, editPanel, editHari]);

  return (
    <div style={{ padding: 20, overflow: 'auto', maxHeight: '100vh', maxWidth: '100vw' }}>
      {onBack && (
        <button onClick={onBack} style={{
          marginBottom: 12, padding: '6px 14px', fontSize: 13,
          background: '#1976D2', color: '#fff', border: 'none',
          borderRadius: 4, cursor: 'pointer',
        }}>
          ← 메인으로
        </button>
      )}
      <h2>HariClockwiseTest — 시계방향(↻) 패널 조립 (가로 {H} × 세로 {V})</h2>
      <div style={{ display: 'flex', gap: 8, margin: '4px 0 12px' }}>
        {([['left', '좌측'], ['bottom', '하단'], ['right', '우측'], ['top', '상단']] as const).map(([ws, name]) => (
          <button key={ws} disabled={haris.some(h => h.wallSide === ws)}
            onClick={() => setHaris(prev => [...prev, { id: `mid_${ws}_${Date.now()}`, hariType: 'middle', wallSide: ws, position: 2000, width: 1200, height: 1500 }])}
            style={{
              padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontWeight: 'bold',
              border: '1px solid #1565c0', background: '#fff', color: '#1565c0',
            }}>
            + {name} 하리
          </button>
        ))}
        {([['topLeft', '좌상'], ['topRight', '우상'], ['bottomLeft', '좌하'], ['bottomRight', '우하']] as const).map(([c, name]) => (
          <button key={c} disabled={haris.some(h => h.hariType === 'corner' && h.corner === c)}
            onClick={() => setHaris(prev => [...prev, { id: `corner_${c}_${Date.now()}`, hariType: 'corner', corner: c, width: 1000, height: 1000 }])}
            style={{
              padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontWeight: 'bold',
              border: '1px solid #9c27b0', background: '#fff', color: '#9c27b0',
            }}>
            + {name} 모서리
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {(['left', 'bottom', 'right', 'top'] as const).map(ws => {
          const exists = haris.some(h => h.wallSide === ws);
          const name = ws === 'left' ? '좌측' : ws === 'bottom' ? '하단' : ws === 'right' ? '우측' : '상단';
          return (
            <button key={ws} disabled={exists}
              onClick={() => setHaris(prev => [...prev, { id: `mid_${ws}_${Date.now()}`, hariType: 'middle', wallSide: ws, position: 2000, width: 1200, height: 1500 }])}
              style={{
                padding: '6px 14px', borderRadius: 4, fontWeight: 'bold', cursor: exists ? 'default' : 'pointer',
                border: exists ? '1px solid #ddd' : '1px solid #1565c0',
                background: exists ? '#f5f5f5' : '#fff', color: exists ? '#aaa' : '#1565c0',
              }}>
              + {name} 하리
            </button>
          );
        })}
      </div>
      <canvas ref={canvasRef}
        style={{ border: '1px solid #ccc', cursor: draggingHari ? 'grabbing' : 'pointer' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp} />

      <h2 style={{ marginTop: 40 }}>반시계방향(↺) 패널 조립 (가로 {H} × 세로 {V})</h2>
      <canvas ref={ccwCanvasRef}
        style={{ border: '1px solid #ccc', cursor: ccwDraggingHari ? 'grabbing' : 'pointer' }}
        onMouseDown={handleCcwMouseDown}
        onMouseMove={handleCcwMouseMove}
        onMouseUp={handleCcwMouseUp}
      />

      {editPanel && (
        <PanelEditDialog
          panel={editPanel}
          lt={editPanelLt}
          rt={editPanelRt}
          // 상대 끝 타입 (CW 패널만 — 오버라이드는 CW에만 적용)
          sizeExtraError={rebalanceError}
          onOpenSegment={editPanelIsCW && findSegOf(editPanel.num) ? () => {
            const sg = findSegOf(editPanel.num);
            setEditPanel(null);
            setSegDialog(sg);
          } : undefined}
          ltPartner={editPanelIsCW ? partnerOf('lt') : null}
          rtPartner={editPanelIsCW ? partnerOf('rt') : null}
          size={editPanelSize}
          onLtChange={setEditPanelLt}
          onRtChange={setEditPanelRt}
          onSizeChange={setEditPanelSize}
          onSave={() => {
            setPanelOverrides(buildOverrides(editPanel, editPanelLt, editPanelRt, editPanelSize));
            setEditPanel(null);
          }}
          onCancel={() => setEditPanel(null)}
        />
      )}

      {segDialog && (
        <SegmentFillDialog
          key={segDialog.seg.key}
          label={segDialog.label}
          total={segDialog.seg.total}
          orient={segDialog.seg.side === 'top' || segDialog.seg.side === 'bottom' ? 'h' : 'v'}
          initial={segRules[segDialog.seg.key] ?? defaultRule(FILL_SPEC)}
          onPreview={rule => setPreviewSegRules(rule ? { ...segRules, [segDialog.seg.key]: rule } : null)}
          onApply={rule => applySegmentRule(segDialog.seg, rule)}
          onCancel={() => { setPreviewSegRules(null); setSegDialog(null); }}
          spec={FILL_SPEC}
        />
      )}

      {editHari && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100
        }}
          onMouseDown={e => { hariOverlayDown.current = e.target === e.currentTarget; }}
          onClick={e => { if (hariOverlayDown.current && e.target === e.currentTarget) setEditHari(null); }}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: 24, minWidth: 280, boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            transform: `translate(${hariDlgDrag.offset.x}px, ${hariDlgDrag.offset.y}px)`,
          }} onClick={e => e.stopPropagation()}>
            <h3 onMouseDown={hariDlgDrag.onMouseDown} style={{ margin: '0 0 16px', cursor: 'move', userSelect: 'none' }}>
              하리 사이즈 변경 ({editHari.hariType === 'corner'
                ? (editHari.corner === 'topLeft' ? '좌상 모서리' : editHari.corner === 'topRight' ? '우상 모서리' : editHari.corner === 'bottomLeft' ? '좌하 모서리' : '우하 모서리')
                : (editHari.wallSide === 'left' ? '좌측벽' : editHari.wallSide === 'right' ? '우측벽' : editHari.wallSide === 'top' ? '상단벽' : '하단벽')})
            </h3>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold' }}>가로 (벽 방향, mm)</label>
              <input type="number" value={editWidth} onChange={e => setEditWidth(e.target.value)} onFocus={selectAllOnFocus} autoFocus
                style={{ width: '100%', padding: 8, fontSize: 16, border: '1px solid #ccc', borderRadius: 4 }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold' }}>세로 (돌출 방향, mm)</label>
              <input type="number" value={editHeight} onChange={e => setEditHeight(e.target.value)} onFocus={selectAllOnFocus}
                style={{ width: '100%', padding: 8, fontSize: 16, border: '1px solid #ccc', borderRadius: 4 }} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
              <button onClick={() => { setHaris(prev => prev.filter(h => h.id !== editHari.id)); setEditHari(null); }}
                style={{ padding: '8px 16px', borderRadius: 4, border: 'none', background: '#e53935', color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}>삭제</button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setEditHari(null)}
                  style={{ padding: '8px 16px', borderRadius: 4, border: '1px solid #ccc', cursor: 'pointer' }}>취소</button>
                <button onClick={handleSave}
                  style={{ padding: '8px 16px', borderRadius: 4, border: 'none', background: '#1565c0', color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}>적용</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HariClockwiseTest;
